import React, { useState } from 'react';
import { Goal } from '../../types/Goal';
import { RelationshipType } from '../../types/Relationship';
import './GoalManagement.css';

interface GoalDependencyFormProps {
  goals: Goal[];
  onSave: (fromGoalId: string, toGoalId: string, relationshipType: string, notes?: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const GoalDependencyForm: React.FC<GoalDependencyFormProps> = ({
  goals,
  onSave,
  onCancel,
  loading = false
}) => {
  const [fromGoalId, setFromGoalId] = useState<string>('');
  const [toGoalId, setToGoalId] = useState<string>('');
  const [relationshipType, setRelationshipType] = useState<string>(RelationshipType.PARENT_CHILD);
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fromGoalId || !toGoalId) {
      setError('Please select both source and target goals');
      return;
    }

    if (fromGoalId === toGoalId) {
      setError('Source and target goals cannot be the same');
      return;
    }

    try {
      await onSave(fromGoalId, toGoalId, relationshipType, notes || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to create dependency');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    }}>
      <div className="modal-content goal-dependency-form-modal">
        <div className="modal-header">
          <h2>Add Goal Dependency</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="goal-form">
          {error && (
            <div className="error-banner" style={{ marginBottom: '1rem' }}>
              <span>{error}</span>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="fromGoal">From Goal *</label>
            <select
              id="fromGoal"
              value={fromGoalId}
              onChange={(e) => setFromGoalId(e.target.value)}
              required
            >
              <option value="">Select a goal...</option>
              {goals.map(goal => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="toGoal">To Goal *</label>
            <select
              id="toGoal"
              value={toGoalId}
              onChange={(e) => setToGoalId(e.target.value)}
              required
            >
              <option value="">Select a goal...</option>
              {goals
                .filter(goal => goal.id !== fromGoalId)
                .map(goal => (
                  <option key={goal.id} value={goal.id}>
                    {goal.title}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="relationshipType">Relationship Type *</label>
            <select
              id="relationshipType"
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value)}
              required
            >
              <option value={RelationshipType.PARENT_CHILD}>Parent/Child</option>
              <option value={RelationshipType.MILESTONE}>Milestone</option>
            </select>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
              {relationshipType === RelationshipType.PARENT_CHILD
                ? 'The source goal is a parent or prerequisite of the target goal'
                : 'The target goal is a milestone-based follow-up of the source goal'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this dependency..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Creating...' : 'Create Dependency'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalDependencyForm;

